import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
const Lottie = lazy(() => import("lottie-react"));
import joinAnimation from "../assets/json/joinAnimation.json";
import president from "../assets/Img/airaa-president-dev-mohanty.webp";
import vicepresident from "../assets/Img/airaa-vice-president-suman-jetly.webp";
import airaaNancy from "../assets/Img/airaa-member-nancy.webp";
import christinaLim from "../assets/Img/airaa-member-christina.webp";
import michelle from "../assets/Img/airaa-member-michelle.webp";
import nana from "../assets/Img/airaa-member-nana.webp";
import SalmanSalequin from "../assets/Img/airaa-member-salman-salequin.webp";
import ManuelaToma from "../assets/Img/airaa-member-manuela.webp";
import rilah from "../assets/Img/airaa-member-rilah.webp";
import BrigadierAdarshDhawan from "../assets/Img/airaa-brigadier-adarsh-dhawan.webp";
import slider0 from "../assets/Img/airaa-hero-recruitment-india.webp";
import slider1 from "../assets/Img/airaa-hero-skill-development.webp";
import slider2 from "../assets/Img/airaa-hero-workforce-empowerment.webp";
import slider3 from "../assets/Img/airaa-hero-overseas-employment.webp";
import slider4 from "../assets/Img/Gallary/B2Bmeeting/airaa-b2b-meeting-brics-wba-1.webp";
import { Link } from "react-router-dom";

const slides = [
  {
    src: slider0,
    caption: "Building career and civilization",
    sub: "AIRAA Chairman Dr. Dev Mohanty meets Odisha CM Shri Mohan Chandra Majhi on Skill Development Initiatives.",
  },
  {
    src: slider3,
    caption: "AIRAA Participated on the CII Global",
    sub: "AIRAA Participated on the CII Global Summit on Skill Development",
  },
  {
    src: slider4,
    caption: "B2B Meeting — AIRAA & BRICS WBA, 9th Feb 2026",
    sub: "When vision meets collaboration, borders dissolve and opportunities multiply together.",
  },
  { src: slider1, caption: "empowerStudents", sub: "empowerDescription" },
  { src: slider2, caption: "modernTrade", sub: "modernDescription" },
];

const members = [
  { src: president, title: "Dr. Dev Mohanty", description: "Chairman/President, AIRAA" },
  { src: vicepresident, title: "Mr. Suman Jetly", description: "Vice President, AIRAA" },
  { src: BrigadierAdarshDhawan, title: "Brig. Adarsh Dhawan", description: "VP – North Region, AIRAA" },
  { src: airaaNancy, title: "Ms. Nancy R", description: "General Secretary, AIRAA" },
  { src: michelle, title: "Ms. Michelle", description: "Office Secretary, AIRAA" },
  { src: ManuelaToma, title: "Ms. Manuela Toma", description: "AIRAA Member – Romania" },
  { src: christinaLim, title: "Ms. Christina Lim", description: "AIRAA Member – Philippines" },
  { src: nana, title: "Mr. Nana Boadu", description: "AIRAA Member – Ghana" },
  { src: rilah, title: "Mr. Ahmed Rilah", description: "AIRAA Member – Sri Lanka" },
  { src: SalmanSalequin, title: "Mr. Salman Salequin", description: "AIRAA Member – Bangladesh" },
];

function HeroHeaderHomePage() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((idx, dir) => {
    setDirection(dir);
    setCurrent(idx);
  }, []);

  const next = useCallback(() => {
    goTo((current + 1) % slides.length, 1);
  }, [current, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length, -1);
  }, [current, goTo]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, 4000);
    return () => clearInterval(id);
  }, [next, paused]);

  const imgVariants = {
    enter: (d) => ({ x: d > 0 ? "100%" : "-100%", opacity: 0 }),
    center: { x: 0, opacity: 1, transition: { duration: 0.65, ease: [0.25, 0.46, 0.45, 0.94] } },
    exit: (d) => ({ x: d > 0 ? "-100%" : "100%", opacity: 0, transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] } }),
  };

  return (
    <div className="py-4 sm:py-6 md:py-8 mt-4 md:mt-2">
      {/* SEO headline */}
      <div className="text-blue-800 text-center mb-4 px-4">
        <h1 className="text-sm md:text-base font-semibold leading-snug">
          AIRAA is India's leading recruitment agency association with 1700+ members — join India's trusted network of placement agencies and HR professionals.
        </h1>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Slider */}
        <div
          className="md:w-2/3 relative rounded-2xl overflow-hidden shadow-xl"
          style={{ aspectRatio: "16/9" }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <AnimatePresence custom={direction} initial={false}>
            <motion.img
              key={current}
              custom={direction}
              variants={imgVariants}
              initial="enter"
              animate="center"
              exit="exit"
              src={slides[current].src}
              alt={slides[current].caption}
              width="900"
              height="508"
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
              fetchPriority={current === 0 ? "high" : "auto"}
            />
          </AnimatePresence>

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />

          {/* Caption */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`caption-${current}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="absolute bottom-4 left-4 right-16 pr-2"
              style={{
                backdropFilter: "blur(12px)",
                background: "linear-gradient(to right, rgba(0,0,0,0.65), rgba(0,0,0,0.3))",
                border: "1px solid rgba(255,255,255,0.15)",
                borderLeft: "4px solid #22c55e",
                borderRadius: "10px",
                padding: "10px 16px",
              }}
            >
              <p className="text-white font-semibold text-sm md:text-base leading-snug">
                {t(slides[current].caption)}
              </p>
              <p className="text-green-300 text-xs md:text-sm mt-1 italic leading-snug">
                {t(slides[current].sub)}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Nav buttons */}
          <div className="absolute bottom-4 right-4 flex gap-2">
            <button
              onClick={prev}
              className="w-11 h-11 flex items-center justify-center rounded-full bg-blue-600/80 hover:bg-blue-700 text-white shadow-lg transition-colors duration-200 active:scale-90 hover:scale-110"
              aria-label="Previous slide"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={next}
              className="w-11 h-11 flex items-center justify-center rounded-full bg-blue-600/80 hover:bg-blue-700 text-white shadow-lg transition-colors duration-200 active:scale-90 hover:scale-110"
              aria-label="Next slide"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Dot indicators */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i, i > current ? 1 : -1)}
                className="p-3 -m-3 flex items-center justify-center focus:outline-none"
                aria-label={`Go to slide ${i + 1}`}
              >
                <span className={`rounded-full transition-all duration-300 block ${
                  i === current ? "w-5 h-2 bg-white" : "w-2 h-2 bg-white/40 hover:bg-white/70"
                }`} />
              </button>
            ))}
          </div>
        </div>

        {/* Right panel */}
        <div className="md:w-1/3 flex flex-col gap-4">
          {/* Join AIRAA card */}
          <div className="relative bg-white rounded-2xl shadow-lg border-b-4 border-blue-500 overflow-hidden">
            <div className="flex items-center">
              <div className="flex-1 p-5">
                <p className="text-xs text-gray-600 uppercase tracking-widest font-medium mb-1">Membership</p>
                <span className="text-2xl font-extrabold text-blue-700 leading-tight block">Join AIRAA Today!</span>
                <p className="text-gray-600 text-sm mt-1">Be part of India's largest recruitment network.</p>
              </div>
              <div className="w-28 mr-2" aria-hidden="true">
                <Suspense fallback={<div className="w-28 h-20" />}>
                  <Lottie animationData={joinAnimation} loop />
                </Suspense>
              </div>
            </div>
            <div className="px-5 pb-4">
              <Link
                to="/registration"
                className="inline-flex items-center gap-2 bg-green-700 hover:bg-green-800 text-white text-sm font-semibold px-5 py-2 rounded-full shadow-md transition-colors duration-200"
              >
                Register Now →
              </Link>
            </div>
          </div>

          {/* Latest Updates — fixed-height clipped scroll, GPU composited */}
          <div className="bg-white rounded-2xl shadow-lg border-b-4 border-blue-500 overflow-hidden flex flex-col flex-1">
            <div className="bg-blue-600 text-white px-4 py-3 flex-shrink-0">
              <span className="font-bold text-base tracking-wide">Latest Updates</span>
            </div>
            {/* overflow:hidden + fixed height — flex-1 must NOT be here or it overrides height */}
            <div style={{ height: '15rem', overflow: 'hidden', flexShrink: 0 }} className="px-4 py-3">
              <div className="marquee-track text-sm text-blue-800 font-medium leading-7">
                <p className="mb-3">✅ Rationalization of FE Registration Process</p>
                <p className="mb-3">✅ Russia: Myanmar to send construction workers to Russia in April 2025.</p>
                <p className="mb-3">✅ Visit of External Affairs Minister to the United Kingdom.</p>
                <p className="mb-3">✅ 3rd Foreign Office Consultations between India &amp; Cambodia in Siem Reap.</p>
                <p className="mb-3">✅ Official Visit of Prime Minister of New Zealand to India</p>
                <p className="mb-3">✅ 3rd FOC between India &amp; Cambodia, Siem Reap</p>
                {/* Duplicate for seamless loop */}
                <p className="mb-3">✅ Rationalization of FE Registration Process</p>
                <p className="mb-3">✅ Russia: Myanmar to send construction workers to Russia in April 2025.</p>
                <p className="mb-3">✅ Visit of External Affairs Minister to the United Kingdom.</p>
                <p className="mb-3">✅ 3rd Foreign Office Consultations between India &amp; Cambodia in Siem Reap.</p>
                <p className="mb-3">✅ Official Visit of Prime Minister of New Zealand to India</p>
                <p className="mb-3">✅ 3rd FOC between India &amp; Cambodia, Siem Reap</p>
              </div>
            </div>
            <div className="px-4 pb-4 pt-1 mt-auto">
              <Link
                to="/news"
                className="block w-full py-2 bg-green-700 hover:bg-green-800 text-white text-sm font-semibold rounded-full shadow-md transition-colors duration-200 text-center"
              >
                View All News
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Member cards */}
      <div className="mt-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {members.slice(0, 10).map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.06, duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] }}
              whileHover={{ y: -4, scale: 1.03 }}
              className="bg-white rounded-2xl p-4 shadow-md text-center border-b-4 border-green-500 transition-shadow duration-300 hover:shadow-xl"
            >
              <img
                src={item.src}
                alt={item.title}
                width="64"
                height="64"
                loading="lazy"
                decoding="async"
                className="w-16 h-16 object-cover rounded-full mx-auto border-2 border-green-400 mb-3"
              />
              <h2 className="text-sm font-bold text-blue-900 leading-tight">{item.title}</h2>
              <p className="text-xs text-gray-600 mt-0.5 leading-snug">{item.description}</p>
            </motion.div>
          ))}
        </div>
        <div className="flex justify-end mt-3">
          <Link
            to="/members"
            className="inline-flex items-center px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-all duration-200 hover:scale-105 active:scale-95 shadow-sm"
          >
            See All Members →
          </Link>
        </div>
      </div>
    </div>
  );
}

export default HeroHeaderHomePage;
